"use client";

import styles from "./main-dashboard.module.css";
import cn from "classnames";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Button,
  message,
  Modal,
  Table,
  TableColumnsType,
  TablePaginationConfig,
  Tabs,
} from "antd";
import { useWindowSize } from "@/hooks/window-size";
import { MainChart } from "@/components/charts/main-chart-apache";
import { useAppContext } from "@/context/AppContext";
import { AppConstants } from "@/app/constants";
import { IntervalSelector } from "@/components/charts/time-interval-selector";
import { CoinSelectorWithMap } from "@/components/coin-selector/coin-selector";
import { COIN_SELECTOR_LAYOUT_PROPS_STANDARD } from "@/components/coin-selector/layout-helper";
import {
  buildAuthPathWithNext,
  buildPathWithCoin,
  buildPathnameWithSearch,
  checkSessionError,
  clearSession,
  getCoinFromSearchParams,
  mapFromRecord,
  provideSession,
  resolveCoinSelection,
  TimeInterval,
} from "@/app/utils";
import { isBackendUnavailableStatus } from "@/app/shared/api-connection-health";
import { getBaseCoinName } from "@/app/shared/coin-utils";
import { guardSessionMainInfo } from "@/app/page-guard";
import {
  IDomainFoundBlock,
  IDomainPoolStat,
  IDomainPoolStatHistories,
} from "@/data/domain/data";
import { ICoin } from "@/data/api/main-data";
import {
  provideDataColumns,
  PAGINATION_THRESHHOLD,
} from "@/components/page-main/shared";
import {
  fetchFoundBlocksWithCount,
  fetchFoundBlocksWithOffsetHash,
  MAIN_HISTORY_MAX_INTERVALS,
  fetchPoolLuck,
  fetchPoolStats,
  fetchPoolStatsHistory,
} from "@/api/fetcher/page-main-fetcher";
import {
  mergeHistoryByInterval,
  normalizeHistoryPoints,
} from "@/data-converters/chart-history";
import {
  SkeletonChart,
  SkeletonCoinSelector,
  SkeletonPoolLuck,
  SkeletonTimeIntervalSelector,
} from "@/components/skeletons/skeleton";
import {
  readNumberLocalStorage as readNumberUiState,
  writeNumberLocalStorage as writeNumberUiState,
} from "@/app/shared/storage-utils";
import {
  buildChartHistoryView,
  createChartHistoryCacheStore,
  rescaleChartHistoryPoints,
  resolveChartHistoryFetchPlan,
  type IChartHistoryCacheEntry,
  type IChartHistoryFetchPlan,
  type IChartHistorySnapshot,
} from "@/app/shared/chart-history-core";

const DEFAULT_BLOCKS_COUNT_STEP = 20;
const MAIN_CHART_CACHE_KEY_PREFIX = "main:hashrate-chart";
const MAIN_CHART_INTERVAL_KEY = "ui:main:chart-time-interval";
const MAIN_HISTORY_INCREMENT_OVERLAP_INTERVALS = 4;
const MAIN_HISTORY_STALE_INTERVALS =
  MAIN_HISTORY_MAX_INTERVALS - MAIN_HISTORY_INCREMENT_OVERLAP_INTERVALS;
const MAIN_HISTORY_CACHE_MAX_KEYS = 64;
type MainHistoryFetchPlan = IChartHistoryFetchPlan;

type IMainPoolStatsSnapshot = IChartHistorySnapshot;
type IMainHistoryCacheEntry = IChartHistoryCacheEntry;
const mainHistoryCacheStore = createChartHistoryCacheStore({
  maxKeys: MAIN_HISTORY_CACHE_MAX_KEYS,
  maxIntervals: MAIN_HISTORY_MAX_INTERVALS,
});

type MainCardInfoKey =
  | "pps"
  | "fees"
  | "yield"
  | "autoExchange"
  | "hashrate"
  | "clients"
  | "workers";

type MainInfoTabKey = "blocks" | "luck";

const MAIN_CARD_INFO_META: Record<
  MainCardInfoKey,
  { titleId: string; descriptionId: string }
> = {
  pps: {
    titleId: "main-coin-pps-available",
    descriptionId: "main-card-info-pps-desc",
  },
  fees: {
    titleId: "main-coin-fees",
    descriptionId: "main-card-info-fees-desc",
  },
  yield: {
    titleId: "settings-coin-info-fpps-yield-title",
    descriptionId: "main-card-info-yield-desc",
  },
  autoExchange: {
    titleId: "main-coin-auto-exchange",
    descriptionId: "main-card-info-auto-exchange-desc",
  },
  hashrate: {
    titleId: "title-hashrate",
    descriptionId: "main-card-info-hashrate-desc",
  },
  clients: {
    titleId: "title-clients",
    descriptionId: "main-card-info-clients-desc",
  },
  workers: {
    titleId: "title-workers",
    descriptionId: "main-card-info-workers-desc",
  },
};

export type MainDashboardMode = "logged" | "public";

export function MainDashboard({ mode }: { mode: MainDashboardMode }) {
  const isPublic = mode == "public";

  const appContext = useAppContext();
  const refreshCoinsInfoNow = appContext.refreshCoinsInfoNow;
  const windowSize = useWindowSize();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const coinFromQuery = getCoinFromSearchParams(searchParams);
  const intl = useIntl();
  const [messageApi, contextHolder] = message.useMessage();
  const coinUrlSyncInProgressRef = useRef(false);
  const mainFetchCycleKeyRef = useRef<string>("");
  const pageRequestTokenRef = useRef(0);
  const refreshTimerRef = useRef<number | undefined>(undefined);
  const latestPoolStatsSnapshotRef = useRef<IMainPoolStatsSnapshot | undefined>(
    undefined
  );
  const lastBlocksFetchAtRef = useRef<number | undefined>(undefined);
  const previousTimerCounterRef = useRef(0);
  const foundBlocksTableContainerRef = useRef<HTMLDivElement | null>(null);

  const [timerEnabled, setTimerEnabled] = useState(false);
  const [timerCounter, setTimerCounter] = useState(0);
  const [poolLuckInfoOpen, setPoolLuckInfoOpen] = useState(false);
  const [infoTab, setInfoTab] = useState<MainInfoTabKey>("blocks");

  const pause = useCallback(() => {
    if (refreshTimerRef.current == undefined) return;
    window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = undefined;
  }, []);

  const restartTimer = useCallback(() => {
    pause();
    if (typeof window == "undefined") return;
    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = undefined;
      setTimerCounter((prev) => prev + 1);
    }, AppConstants.PageInfoLoadInterval * 1000);
  }, [pause]);

  const redirectToAuth = useCallback(() => {
    clearSession();
    appContext.clearContext();
    router.push(buildAuthPathWithNext(pathname, searchParams, coinFromQuery));
  }, [appContext, coinFromQuery, pathname, router, searchParams]);

  const [loadingTable, setLoadingTable] = useState(false);
  const [timeInterval, setTimeInterval] = useState(() =>
    readNumberUiState(
      MAIN_CHART_INTERVAL_KEY,
      TimeInterval.Min15,
      isMainChartInterval
    )
  );
  const [selectedCoin, setSelectedCoin] = useState<string | undefined>(undefined);
  const [selectedAlgorithm, setSelectedAlgorithm] = useState<string | undefined>(
    undefined
  );
  const [poolInfo, setPoolInfo] = useState<IDomainPoolStat[] | undefined>(
    undefined
  );
  const [poolLuck, setPoolLuck] = useState<IDomainPoolStat[] | undefined>(
    undefined
  );
  const [poolStatsHistoryChart, setPoolStatsHistoryChart] = useState<
    IDomainPoolStatHistories | undefined
  >(undefined);
  const [foundBlocksTableContainerNode, setFoundBlocksTableContainerNode] =
    useState<HTMLDivElement | null>(null);
  const [foundBlocksTableWidth, setFoundBlocksTableWidth] = useState(0);
  const [foundBlocksTable, setFoundBlocksTable] = useState<
    IDomainFoundBlock[] | undefined
  >(undefined);
  const [publicLinksBlock, setPublicLinksBlock] = useState<
    Map<string, string> | undefined
  >(undefined);
  const mainChartCacheKey = useMemo(
    () =>
      provideMainChartCacheKey({
        coin: selectedCoin,
        interval: timeInterval,
      }),
    [selectedCoin, timeInterval]
  );

  const hasCoins = appContext.coins != undefined && appContext.coins.size > 0;
  const showSelectorData = isPublic
    ? hasCoins
    : appContext.hasMainInfo && hasCoins;
  const linksBlock = isPublic ? publicLinksBlock : appContext.linksBlock;

  const selectedCoinInfo = useMemo(() => {
    if (!selectedCoin || !appContext.coinsInfo) return undefined;
    return appContext.coinsInfo.get(selectedCoin);
  }, [appContext.coinsInfo, selectedCoin]);

  const statusAvailableLabel = intl.messages["main-status-available"] as string;
  const statusUnavailableLabel = intl.messages[
    "main-status-unavailable"
  ] as string;

  const coinIsAlgorithm =
    selectedCoin != undefined &&
    selectedAlgorithm != undefined &&
    selectedCoin == selectedAlgorithm;

  const ppsAvailabilityMeta = providePpsAvailableMeta(
    selectedCoinInfo,
    coinIsAlgorithm,
    statusAvailableLabel,
    statusUnavailableLabel
  );
  const selectedCoinLabel = useMemo(() => {
    const baseCoin = getBaseCoinName(selectedCoin);
    return baseCoin.length > 0 ? baseCoin.toUpperCase() : "COIN";
  }, [selectedCoin]);
  const feePairMeta = provideFeePairMeta(
    selectedCoinInfo?.pplnsFee,
    selectedCoinInfo?.ppsFee,
    coinIsAlgorithm
  );
  const showFppsFeeLine = selectedCoinInfo?.ppsAvailable !== false;
  const showFppsYieldCard =
    selectedCoinInfo?.ppsAvailable === true && coinIsAlgorithm == false;
  const profitabilityScale = useMemo(
    () => provideProfitabilityScaleMeta(selectedAlgorithm, selectedCoin),
    [selectedAlgorithm, selectedCoin]
  );
  const profitabilityHashrateLabel = `${profitabilityScale.value}${profitabilityScale.unit}`;
  const yieldCoinLabel = `${selectedCoinLabel}/${profitabilityHashrateLabel}`;
  const yieldBtcLabel = `BTC/${profitabilityHashrateLabel}`;
  const yieldUsdLabel = `USD/${profitabilityHashrateLabel}`;
  const expectedFppsCoinYieldLabel = useMemo(
    () => "soon",
    []
  );
  const expectedFppsBtcYieldLabel = useMemo(() => "soon", []);
  const expectedFppsUsdYieldLabel = useMemo(() => "soon", []);
  const autoExchangeMeta = provideAutoExchangeMeta(
    coinIsAlgorithm,
    statusUnavailableLabel
  );
  const hashrateMeta = providePoolStatMeta(poolInfo, "title-hashrate");
  const clientsMeta = providePoolStatMeta(poolInfo, "title-clients");
  const workersMeta = providePoolStatMeta(poolInfo, "title-workers");

  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentRequestCount, setCurrentRequestCount] = useState(
    DEFAULT_BLOCKS_COUNT_STEP
  );
  const [lastPage, setLastPage] = useState(false);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [openedCardInfo, setOpenedCardInfo] = useState<
    MainCardInfoKey | undefined
  >(undefined);
  const openedCardInfoMeta =
    openedCardInfo != undefined ? MAIN_CARD_INFO_META[openedCardInfo] : undefined;
  const infoButtonAriaLabel = intl.messages[
    "main-card-info-open"
  ] as string;

  const providePagination = useCallback(
    (
      blocks: IDomainFoundBlock[] | undefined
    ): false | TablePaginationConfig => {
      if (blocks == undefined || blocks.length <= PAGINATION_THRESHHOLD)
        return false;

      return {
        size: "small",
        pageSize: pageSize,
        position: ["bottomLeft"],
        pageSizeOptions: [10, 25, 50, 100],
        showSizeChanger: true,
        current: currentPage,
        onChange(pageNumber, pageSize) {
          const isLastPage = foundBlocksTable!.length / pageSize == pageNumber;
          setCurrentPage(pageNumber);
          setPageSize(pageSize);
          setLastPage(isLastPage);
        },
      };
    },
    [currentPage, foundBlocksTable, pageSize]
  );

  const showErrorMessage = useCallback(
    (text: string) => {
      if (!isPublic && !provideSession()) return;
      messageApi.open({ type: "error", content: text });
    },
    [isPublic, messageApi]
  );

  const handleErrorStatus = useCallback(
    (errorStatus: string) => {
      const fullText = `${
        intl.messages["api-response-status-unknown"] as string
      } - ${errorStatus}`;
      showErrorMessage(fullText);
    },
    [intl, showErrorMessage]
  );

  const resetFetchCycleKey = useCallback(() => {
    if (!isPublic) {
      mainFetchCycleKeyRef.current = "";
    }
  }, [isPublic]);

  const handleRequestErrorStatus = useCallback(
    (errorStatus: string): boolean => {
      if (!isPublic && checkSessionError(errorStatus)) {
        resetFetchCycleKey();
        redirectToAuth();
        return true;
      }
      if (isBackendUnavailableStatus(errorStatus)) {
        return false;
      }

      handleErrorStatus(errorStatus);
      return false;
    },
    [handleErrorStatus, isPublic, redirectToAuth, resetFetchCycleKey]
  );

  const startAutoRefresh = useCallback(() => {
    setTimerEnabled((prev) => {
      if (!prev) {
        restartTimer();
      }
      return true;
    });
  }, [restartTimer]);

  const applyPoolStatsHistoryChart = useCallback(
    (nextValue: IDomainPoolStatHistories | undefined) => {
      setPoolStatsHistoryChart(nextValue);
    },
    []
  );

  const loadMoreItems = useCallback(async () => {
    if (selectedCoin == undefined) return;
    setLoadingTable(true);

    const lastHash = foundBlocksTable?.slice(-1)[0]?.hash;
    const lastHeight = foundBlocksTable?.slice(-1)[0]?.height;

    fetchFoundBlocksWithOffsetHash(
      selectedCoin,
      DEFAULT_BLOCKS_COUNT_STEP,
      intl ? intl.locale : "en",
      lastHash!,
      lastHeight!,
      (data) => {
        setCanLoadMore(
          data.length != 0 &&
            (data.length == DEFAULT_BLOCKS_COUNT_STEP ||
              data.length % DEFAULT_BLOCKS_COUNT_STEP == 0)
        );

        const newData = foundBlocksTable?.concat(data);
        setCurrentRequestCount(newData!.length);

        setFoundBlocksTable(newData);
        setLoadingTable(false);
        setLastPage(data.length == 0);
        restartTimer();
      },
      (errorStatus) => {
        setLoadingTable(false);
        pause();
        handleRequestErrorStatus(errorStatus);
      }
    );
  }, [
    foundBlocksTable,
    handleRequestErrorStatus,
    intl,
    pause,
    restartTimer,
    selectedCoin,
  ]);

  const foundBlocksTableLayout = useMemo(
    () =>
      provideDataColumns(
        intl,
        windowSize.width,
        foundBlocksTableWidth,
        foundBlocksTable,
        {
          resolveHashLink: (record) => {
            const link = linksBlock?.get(selectedCoin!);
            if (link == undefined) return undefined;
            return `${link}${record.hash}`;
          },
        }
      ),
    [
      foundBlocksTable,
      foundBlocksTableWidth,
      intl,
      linksBlock,
      selectedCoin,
      windowSize.width,
    ]
  );
  const columns: TableColumnsType<IDomainFoundBlock> =
    foundBlocksTableLayout.columns;

  const handleFoundBlocksTableContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      foundBlocksTableContainerRef.current = node;
      setFoundBlocksTableContainerNode(node);
    },
    []
  );

  const pag = useMemo(
    () => providePagination(foundBlocksTable),
    [foundBlocksTable, providePagination]
  );

  const foot = useMemo(
    () =>
      provideFooter(
        lastPage,
        loadingTable,
        canLoadMore,
        currentPage,
        loadMoreItems
      ),
    [canLoadMore, currentPage, lastPage, loadMoreItems, loadingTable]
  );

  const chartWidth = useMemo(
    () => provideChartWidth(windowSize.width, isPublic),
    [isPublic, windowSize.width]
  );

  const isMobile = windowSize.width <= 768;
  const useSelectInterval = isMobile || chartWidth < 560;

  const handleCoinSelection = useCallback(
    (alg: string, coin: string) => {
      setCurrentRequestCount(DEFAULT_BLOCKS_COUNT_STEP);
      setCanLoadMore(true);
      setCurrentPage(1);
      setPoolLuck(undefined);
      setInfoTab("blocks");
      setSelectedAlgorithm(alg);
      setSelectedCoin(coin);
      appContext.rememberCoinSelection(alg, coin);

      if (!isPublic) {
        coinUrlSyncInProgressRef.current = true;
        const nextPath = buildPathWithCoin(pathname, searchParams, coin);
        const currentPath = buildPathnameWithSearch(pathname, searchParams);
        if (nextPath != currentPath) {
          router.replace(nextPath, { scroll: false });
        } else {
          coinUrlSyncInProgressRef.current = false;
        }
      }

      restartTimer();
    },
    [
      appContext,
      isPublic,
      pathname,
      restartTimer,
      router,
      searchParams,
    ]
  );

  const handleTimeIntervalSelection = useCallback(
    (interval: number) => {
      if (!isMainChartInterval(interval)) return;
      setTimeInterval(interval);
      writeNumberUiState(MAIN_CHART_INTERVAL_KEY, interval);
      restartTimer();
    },
    [restartTimer]
  );
  const handleOpenCardInfo = useCallback((card: MainCardInfoKey) => {
    setOpenedCardInfo(card);
  }, []);
  const handleCloseCardInfo = useCallback(() => {
    setOpenedCardInfo(undefined);
  }, []);

  useEffect(() => {
    if (mainChartCacheKey == undefined) {
      applyPoolStatsHistoryChart(undefined);
      return;
    }

    const cachedEntry = readMainHistoryCache(mainChartCacheKey);
    if (cachedEntry == undefined) return;

    applyPoolStatsHistoryChart(
      buildMainChartView(cachedEntry, latestPoolStatsSnapshotRef.current)
    );
  }, [applyPoolStatsHistoryChart, mainChartCacheKey]);

  const fetchPageInfo = useCallback((isTimerRefresh = false) => {
    if (selectedCoin == undefined) {
      resetFetchCycleKey();
      pageRequestTokenRef.current += 1;
      return;
    }

    if (!isPublic) {
      const activeInfoTab =
        selectedCoin != selectedAlgorithm ? infoTab : "all";
      const session = provideSession();
      if (!session) {
        resetFetchCycleKey();
        pageRequestTokenRef.current += 1;
        redirectToAuth();
        return;
      }

      const cycleKey = [
        selectedCoin,
        selectedAlgorithm ?? "",
        String(timeInterval),
        String(currentRequestCount),
        intl.locale,
        String(timerCounter),
        isMobile ? "mobile" : "desktop",
        activeInfoTab,
      ].join("|");

      if (mainFetchCycleKeyRef.current == cycleKey) {
        return;
      }
      mainFetchCycleKeyRef.current = cycleKey;
    }
    const requestToken = pageRequestTokenRef.current + 1;
    pageRequestTokenRef.current = requestToken;
    const isStaleRequest = () => pageRequestTokenRef.current != requestToken;

    fetchPoolStats(
      selectedCoin,
      (poolStats, lastPower, currentTime) => {
        if (isStaleRequest()) return;
        setPoolInfo(poolStats);
        latestPoolStatsSnapshotRef.current = {
          currentTime,
          lastPower,
        };

        const cacheEntry =
          mainChartCacheKey != undefined
            ? readMainHistoryCache(mainChartCacheKey)
            : undefined;
        if (cacheEntry != undefined) {
          applyPoolStatsHistoryChart(
            buildMainChartView(
              cacheEntry,
              latestPoolStatsSnapshotRef.current
            )
          );
        }

        const historyFetchPlan = resolveMainHistoryFetchPlan({
          cacheEntry,
          intervalSec: timeInterval,
          currentTimeSec: currentTime,
        });

        if (historyFetchPlan.type == "none") {
          startAutoRefresh();
          return;
        }

        fetchPoolStatsHistory(
          selectedCoin,
          timeInterval,
          (poolStatsHistories, historyCurrentTime) => {
            if (isStaleRequest()) return;
            const incomingHistories = normalizeHistoryPoints(
              poolStatsHistories.histories,
              MAIN_HISTORY_MAX_INTERVALS
            );
            const previousBaseHistories =
              historyFetchPlan.type == "incremental" &&
              cacheEntry != undefined &&
              cacheEntry.baseHistories.length > 0
                ? rescaleChartHistoryPoints(
                    cacheEntry.baseHistories,
                    cacheEntry.suffix,
                    poolStatsHistories.suffix
                  )
                : undefined;
            const nextBaseHistories =
              previousBaseHistories != undefined &&
              previousBaseHistories.length > 0
                ? mergeHistoryByInterval({
                    previous: previousBaseHistories,
                    incoming: incomingHistories,
                    groupByIntervalSec: timeInterval,
                    maxPoints: MAIN_HISTORY_MAX_INTERVALS,
                  })
                : incomingHistories;

            const nextCacheEntry: IMainHistoryCacheEntry = {
              suffix: poolStatsHistories.suffix,
              baseHistories: nextBaseHistories,
              historyCurrentTime:
                Number.isFinite(historyCurrentTime) && historyCurrentTime > 0
                  ? Math.floor(historyCurrentTime)
                  : currentTime,
            };
            if (mainChartCacheKey != undefined) {
              writeMainHistoryCache(mainChartCacheKey, nextCacheEntry);
            }
            applyPoolStatsHistoryChart(
              buildMainChartView(
                nextCacheEntry,
                latestPoolStatsSnapshotRef.current
              )
            );
            startAutoRefresh();
          },
          (errorStatus) => {
            if (isStaleRequest()) return;
            resetFetchCycleKey();
            pause();
            handleRequestErrorStatus(errorStatus);
          },
          historyFetchPlan.type == "incremental"
            ? {
                timeFrom: historyFetchPlan.timeFrom,
                overlapIntervals: MAIN_HISTORY_INCREMENT_OVERLAP_INTERVALS,
              }
            : undefined
        );
      },
      (errorStatus) => {
        if (isStaleRequest()) return;
        resetFetchCycleKey();
        pause();
        handleRequestErrorStatus(errorStatus);
      }
    );

    if (selectedCoin == selectedAlgorithm) return;

    const shouldFetchPoolLuck = infoTab == "luck";
    const shouldFetchBlocks = infoTab == "blocks";

    if (shouldFetchPoolLuck) {
      fetchPoolLuck(
        selectedCoin,
        (data) => {
          if (isStaleRequest()) return;
          setPoolLuck(data);
        },
        (errorStatus) => {
          if (isStaleRequest()) return;
          resetFetchCycleKey();
          pause();
          handleRequestErrorStatus(errorStatus);
        }
      );
    }

    if (shouldFetchBlocks) {
      const now = Date.now();
      const shouldSkipTimerBlocksRefresh =
        isTimerRefresh &&
        lastBlocksFetchAtRef.current != undefined &&
        now - lastBlocksFetchAtRef.current <
          AppConstants.FoundBlocksLoadInterval * 1000;

      if (shouldSkipTimerBlocksRefresh) {
        setLoadingTable(false);
        return;
      }

      lastBlocksFetchAtRef.current = now;
      setLoadingTable(true);
      fetchFoundBlocksWithCount(
        selectedCoin,
        currentRequestCount,
        intl ? intl.locale : "en",
        (data) => {
          if (isStaleRequest()) return;
          setCanLoadMore(
            data.length != 0 &&
              (data.length == DEFAULT_BLOCKS_COUNT_STEP ||
                data.length % DEFAULT_BLOCKS_COUNT_STEP == 0)
          );
          setLastPage(false);
          setFoundBlocksTable(data);
          setLoadingTable(false);
        },
        (errorStatus) => {
          if (isStaleRequest()) return;
          resetFetchCycleKey();
          setLoadingTable(false);
          pause();
          handleRequestErrorStatus(errorStatus);
        }
      );
    } else {
      setLoadingTable(false);
    }
  }, [
    applyPoolStatsHistoryChart,
    currentRequestCount,
    handleRequestErrorStatus,
    intl,
    isMobile,
    isPublic,
    infoTab,
    mainChartCacheKey,
    pause,
    redirectToAuth,
    resetFetchCycleKey,
    selectedAlgorithm,
    selectedCoin,
    startAutoRefresh,
    timeInterval,
    timerCounter,
  ]);

  useEffect(() => {
    if (!isPublic) return;

    fetch("/row-links/links-block.json")
      .then((res) => res.json())
      .then((data) => {
        const links = mapFromRecord<string>(data as Record<string, string>);
        setPublicLinksBlock(links);
      })
      .catch(() => {
        setPublicLinksBlock(new Map());
      });
  }, [isPublic]);

  useEffect(() => {
    if (!isPublic) return;

    const resolvedSelection = resolveCoinSelection(
      appContext.coins,
      appContext.rememberedAlgorithm,
      appContext.rememberedCoin
    );
    if (!resolvedSelection) return;

    const algorithmChanged = resolvedSelection.algorithm != selectedAlgorithm;
    const coinChanged = resolvedSelection.coin != selectedCoin;

    if (algorithmChanged) setSelectedAlgorithm(resolvedSelection.algorithm);
    if (coinChanged) setSelectedCoin(resolvedSelection.coin);
    if (algorithmChanged || coinChanged) {
      setPoolLuck(undefined);
      setInfoTab("blocks");
    }

    if (algorithmChanged || coinChanged) {
      appContext.rememberCoinSelection(
        resolvedSelection.algorithm,
        resolvedSelection.coin
      );
      setTimerEnabled(true);
      restartTimer();
    }
  }, [
    appContext,
    appContext.coins,
    appContext.rememberedAlgorithm,
    appContext.rememberedCoin,
    isPublic,
    restartTimer,
    selectedAlgorithm,
    selectedCoin,
  ]);

  useEffect(() => {
    if (isPublic) return;

    const guardResult = guardSessionMainInfo({
      appContext,
      redirectToAuth,
      onMainInfoError: handleErrorStatus,
    });
    if (guardResult.status != "ready") return;

    if (coinUrlSyncInProgressRef.current) {
      if (coinFromQuery != undefined && selectedCoin == coinFromQuery) {
        coinUrlSyncInProgressRef.current = false;
      }
      return;
    }

    const resolvedSelection = resolveCoinSelection(
      appContext.coins,
      coinFromQuery ? undefined : appContext.rememberedAlgorithm,
      coinFromQuery ?? appContext.rememberedCoin
    );
    if (!resolvedSelection) return;

    const nextPath = buildPathWithCoin(pathname, searchParams, resolvedSelection.coin);
    const currentPath = buildPathnameWithSearch(pathname, searchParams);
    if (nextPath != currentPath) {
      router.replace(nextPath, { scroll: false });
      return;
    }

    setSelectedAlgorithm(resolvedSelection.algorithm);
    setSelectedCoin(resolvedSelection.coin);
    setPoolLuck(undefined);
    setInfoTab("blocks");
    appContext.rememberCoinSelection(
      resolvedSelection.algorithm,
      resolvedSelection.coin
    );
  }, [
    appContext,
    appContext.coins,
    appContext.fetchingMainInfo,
    appContext.hasMainInfo,
    appContext.rememberedAlgorithm,
    appContext.rememberedCoin,
    coinFromQuery,
    handleErrorStatus,
    isPublic,
    pathname,
    redirectToAuth,
    router,
    searchParams,
    selectedCoin,
  ]);

  useEffect(() => {
    const isTimerRefresh = timerCounter != previousTimerCounterRef.current;
    previousTimerCounterRef.current = timerCounter;
    fetchPageInfo(isTimerRefresh);
  }, [fetchPageInfo, timerCounter]);

  useEffect(() => {
    refreshCoinsInfoNow();
  }, [refreshCoinsInfoNow]);

  useEffect(() => {
    if (timerEnabled == false) return;
    restartTimer();
  }, [restartTimer, timerCounter, timerEnabled]);

  useEffect(() => {
    return () => {
      pause();
      setTimerEnabled(false);
    };
  }, [pause]);

  useEffect(() => {
    setPoolLuck(undefined);
  }, [selectedAlgorithm, selectedCoin]);

  useLayoutEffect(() => {
    if (!foundBlocksTableContainerNode) {
      setFoundBlocksTableWidth(0);
      return;
    }

    const recomputeTableWidth = () => {
      setFoundBlocksTableWidth(
        Math.round(
          foundBlocksTableContainerNode.getBoundingClientRect().width ||
            foundBlocksTableContainerNode.clientWidth ||
            0
        )
      );
    };

    recomputeTableWidth();
    const animationFrameId = window.requestAnimationFrame(recomputeTableWidth);

    const observer =
      typeof ResizeObserver == "undefined"
        ? undefined
        : new ResizeObserver(() => {
            recomputeTableWidth();
          });

    if (observer) {
      observer.observe(foundBlocksTableContainerNode);
    }

    window.addEventListener("resize", recomputeTableWidth);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      observer?.disconnect();
      window.removeEventListener("resize", recomputeTableWidth);
    };
  }, [
    foundBlocksTableContainerNode,
    infoTab,
    selectedAlgorithm,
    selectedCoin,
    windowSize.width,
  ]);

  const renderCardInfoButton = (
    card: MainCardInfoKey,
    disabled = false
  ) => (
    <button
      type="button"
      className={styles.coinFeeInfoButton}
      onClick={disabled ? undefined : () => handleOpenCardInfo(card)}
      aria-label={infoButtonAriaLabel}
      disabled={disabled}
    >
      i
    </button>
  );

  const renderFoundBlocksTable = () => (
    <div
      ref={handleFoundBlocksTableContainerRef}
      className={styles.tableContainer}
    >
      <Table<IDomainFoundBlock>
        columns={columns}
        dataSource={foundBlocksTable != undefined ? foundBlocksTable : []}
        className={styles.dataTable}
        style={{ margin: "1em 0" }}
        size="small"
        loading={loadingTable}
        tableLayout="fixed"
        scroll={{ x: foundBlocksTableLayout.scrollX }}
        pagination={pag}
      />
      {foot}
    </div>
  );

  const renderLuckGrid = () => (
    poolLuck == undefined ? (
      <SkeletonPoolLuck />
    ) : (
      <div className={cn(styles.coinFeeMeta, styles.infoTabLuckGrid)}>
        {poolLuck.map((dataItem) => (
          <div key={dataItem.title} className={styles.coinFeeItem}>
            <span className={styles.coinFeeLabel}>
              <FormattedMessage id={dataItem.title} />
            </span>
            <span
              className={cn(styles.coinFeeValue, styles.coinFeeValueNeutral)}
            >
              {dataItem.value}
            </span>
          </div>
        ))}
      </div>
    )
  );

  const renderLuckTabLabel = () => (
    <span className={styles.infoTabLabel}>
      <FormattedMessage id="title-luck-less-better" />
      <button
        type="button"
        className={styles.infoTabHelpButton}
        aria-label={
          (intl.messages["main-pool-luck-help-open"] as string) ??
          "Open pool luck explanation"
        }
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setPoolLuckInfoOpen(true);
        }}
      >
        ?
      </button>
    </span>
  );

  return (
    <div className={styles.mainWrapper}>
      {contextHolder}

      <div className={styles.surfaceCard}>
        {showSelectorData && (
          <>
            <CoinSelectorWithMap
              coinsMap={appContext.coins!}
              coinsInfo={appContext.coinsInfo}
              selectedAlgorithm={selectedAlgorithm}
              selectedCoin={selectedCoin}
              callback={handleCoinSelection}
              {...COIN_SELECTOR_LAYOUT_PROPS_STANDARD}
            />
            <div className={styles.coinFeeMeta}>
              <div
                className={cn(
                  styles.coinFeeItem,
                  coinIsAlgorithm && styles.coinFeeItemDisabled
                )}
              >
                {renderCardInfoButton("pps")}
                <span className={styles.coinFeeLabel}>
                  <FormattedMessage id="main-coin-pps-available" />
                </span>
                <span
                  className={cn(
                    styles.coinFeeValue,
                    provideMetaToneClass(styles, ppsAvailabilityMeta.tone)
                  )}
                >
                  {ppsAvailabilityMeta.value}
                </span>
              </div>
              <div
                className={cn(
                  styles.coinFeeItem,
                  coinIsAlgorithm && styles.coinFeeItemDisabled
                )}
              >
                {renderCardInfoButton("fees")}
                <span className={styles.coinFeeLabel}>
                  <FormattedMessage id="main-coin-fees" />
                </span>
                <div className={styles.coinFeeValueBlock}>
                  <span
                    className={cn(
                      styles.coinFeeSubValue,
                      provideMetaToneClass(styles, feePairMeta.tone)
                    )}
                  >
                    <span className={styles.coinFeeSubValueLabel}>PPLNS: </span>
                    <span className={styles.coinFeeSubValueValue}>
                      {feePairMeta.pplnsValue}
                    </span>
                  </span>
                  {showFppsFeeLine ? (
                    <span
                      className={cn(
                        styles.coinFeeSubValue,
                        provideMetaToneClass(styles, feePairMeta.tone)
                      )}
                    >
                      <span className={styles.coinFeeSubValueLabel}>FPPS: </span>
                      <span className={styles.coinFeeSubValueValue}>
                        {feePairMeta.ppsValue}
                      </span>
                    </span>
                  ) : (
                    <span
                      className={cn(
                        styles.coinFeeSubValue,
                        styles.coinFeeSubValuePlaceholder
                      )}
                    >
                      <span className={styles.coinFeeSubValueLabel}>FPPS: </span>
                      <span className={styles.coinFeeSubValueValue}>—</span>
                    </span>
                  )}
                </div>
              </div>
              <div
                className={cn(
                  styles.coinFeeItem,
                  coinIsAlgorithm && styles.coinFeeItemDisabled
                )}
              >
                {renderCardInfoButton("autoExchange")}
                <span className={styles.coinFeeLabel}>
                  <FormattedMessage id="main-coin-auto-exchange" />
                </span>
                <span
                  className={cn(
                    styles.coinFeeValue,
                    provideMetaToneClass(styles, autoExchangeMeta.tone)
                  )}
                >
                  {autoExchangeMeta.value}
                </span>
              </div>
            </div>
            <div className={cn(styles.coinFeeMeta, styles.coinFeeMetaSecondary)}>
              <div className={styles.coinFeeItem}>
                {renderCardInfoButton("hashrate")}
                <span className={styles.coinFeeLabel}>
                  <FormattedMessage id="title-hashrate" />
                </span>
                <span
                  className={cn(
                    styles.coinFeeValue,
                    provideMetaToneClass(styles, hashrateMeta.tone)
                  )}
                >
                  {hashrateMeta.value}
                </span>
              </div>
              <div className={styles.coinFeeItem}>
                {renderCardInfoButton("clients")}
                <span className={styles.coinFeeLabel}>
                  <FormattedMessage id="title-clients" />
                </span>
                <span
                  className={cn(
                    styles.coinFeeValue,
                    provideMetaToneClass(styles, clientsMeta.tone)
                  )}
                >
                  {clientsMeta.value}
                </span>
              </div>
              {showFppsYieldCard ? (
                <div
                  className={cn(
                    styles.coinFeeItem,
                    styles.coinFeeItemDisabled
                  )}
                >
                  {renderCardInfoButton("yield", true)}
                  <span className={styles.coinFeeLabel}>
                    <FormattedMessage id="settings-coin-info-fpps-yield-title" />
                  </span>
                  <div className={styles.coinFeeValueBlock}>
                    <span className={styles.coinFeeSubValue}>
                      {`${yieldCoinLabel}: ${expectedFppsCoinYieldLabel}`}
                    </span>
                    <span className={styles.coinFeeSubValue}>
                      {`${yieldBtcLabel}: ${expectedFppsBtcYieldLabel}`}
                    </span>
                    <span className={styles.coinFeeSubValue}>
                      {`${yieldUsdLabel}: ${expectedFppsUsdYieldLabel}`}
                    </span>
                  </div>
                </div>
              ) : (
                <div className={styles.coinFeeItem}>
                  {renderCardInfoButton("workers")}
                  <span className={styles.coinFeeLabel}>
                    <FormattedMessage id="title-workers" />
                  </span>
                  <span
                    className={cn(
                      styles.coinFeeValue,
                      provideMetaToneClass(styles, workersMeta.tone)
                    )}
                  >
                    {workersMeta.value}
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {!showSelectorData && (
          <>
            <SkeletonCoinSelector />
            {!isPublic && (
              <div className={styles.topMarginHalf}>
                <SkeletonTimeIntervalSelector />
              </div>
            )}
          </>
        )}
      </div>

      <div className={cn(styles.surfaceCard, styles.chartWrapper)}>
        {poolStatsHistoryChart == undefined && (
          <SkeletonChart
            w={windowSize && windowSize.width ? chartWidth : 1200}
          />
        )}

        {poolStatsHistoryChart != undefined && (
          <>
            <div className={styles.chartSectionHeader}>
              <h3 className={styles.chartTitle}>
                <FormattedMessage id="title-hashrate-chart" />
              </h3>

              <div className={styles.chartAction}>
                <IntervalSelector
                  callback={handleTimeIntervalSelection}
                  preferSelect={useSelectInterval}
                  value={timeInterval}
                />
              </div>
            </div>

            <MainChart
              w={windowSize && windowSize.width ? chartWidth : 1200}
              values={poolStatsHistoryChart}
            />
          </>
        )}
      </div>

      {selectedCoin && selectedCoin != selectedAlgorithm && (
        <div onClick={(e) => e.stopPropagation()} className={styles.surfaceCard}>
          <Tabs
            activeKey={infoTab}
            onChange={(key) => setInfoTab(key as MainInfoTabKey)}
            className={styles.infoTabs}
            destroyInactiveTabPane={false}
            items={[
              {
                key: "blocks",
                label: <FormattedMessage id="title-found-blocks" />,
                children: renderFoundBlocksTable(),
              },
              {
                key: "luck",
                label: renderLuckTabLabel(),
                children: renderLuckGrid(),
              },
            ]}
          />
        </div>
      )}

      {isPublic &&
        selectedCoin &&
        selectedAlgorithm &&
        selectedCoin == selectedAlgorithm && (
          <div className={styles.surfaceCard}>
            <div className={styles.shortGreeting}>
              <h3 className={styles.shortGreetingTitle}>
                <FormattedMessage id="landing-short-greeting-title" />
              </h3>
              <p className={styles.shortGreetingText}>
                <FormattedMessage id="landing-short-greeting-text" />
              </p>
            </div>
          </div>
        )}

      <Modal
        open={openedCardInfoMeta != undefined}
        title={
          openedCardInfoMeta != undefined ? (
            <FormattedMessage id={openedCardInfoMeta.titleId} />
          ) : null
        }
        onCancel={handleCloseCardInfo}
        footer={[
          <Button key="close" type="primary" onClick={handleCloseCardInfo}>
            <FormattedMessage id="action-close" />
          </Button>,
        ]}
      >
        {openedCardInfoMeta != undefined && (
          <p className={styles.coinInfoModalText}>
            <FormattedMessage id={openedCardInfoMeta.descriptionId} />
          </p>
        )}
      </Modal>

      <Modal
        open={poolLuckInfoOpen}
        title={<FormattedMessage id="title-luck-less-better" />}
        onCancel={() => setPoolLuckInfoOpen(false)}
        footer={[
          <Button
            key="pool-luck-help-ok"
            type="primary"
            onClick={() => setPoolLuckInfoOpen(false)}
          >
            <FormattedMessage id="title-got-it" />
          </Button>,
        ]}
      >
        <div className={styles.infoModalContent}>
          <p className={styles.infoModalText}>
            <FormattedMessage id="main-pool-luck-help-1" />
          </p>
          <p className={styles.infoModalText}>
            <FormattedMessage id="main-pool-luck-help-2" />
          </p>
          <p className={styles.infoModalText}>
            <FormattedMessage id="main-pool-luck-help-3" />
          </p>
          <p className={styles.infoModalText}>
            <FormattedMessage id="main-pool-luck-help-4" />
          </p>
          <p className={styles.infoModalText}>
            <FormattedMessage id="main-pool-luck-help-5" />
          </p>
        </div>
      </Modal>

    </div>
  );
}

function provideChartWidth(windowWidth: number, isPublic: boolean): number {
  if (!isPublic) {
    if (windowWidth > 1450) return 1200 - 16;
    if (windowWidth > 1024) return windowWidth - 16 - 250;
    if (windowWidth > 768) return windowWidth - 16 - 80;
    return Math.max(windowWidth - 56, 260);
  }

  if (windowWidth > 1230) return 1200;
  if (windowWidth > 768) return Math.max(windowWidth - 34, 320);
  return Math.max(windowWidth - 56, 260);
}

function provideMainChartCacheKey({
  coin,
  interval,
}: {
  coin: string | undefined;
  interval: number;
}): string | undefined {
  if (!coin || !Number.isFinite(interval) || interval <= 0) return undefined;

  return [MAIN_CHART_CACHE_KEY_PREFIX, coin, String(Math.floor(interval))].join(
    ":"
  );
}

function isMainChartInterval(value: number): boolean {
  return (
    value == TimeInterval.Min15 ||
    value == TimeInterval.Min30 ||
    value == TimeInterval.Hour1 ||
    value == TimeInterval.Hour4 ||
    value == TimeInterval.Day1
  );
}

function readMainHistoryCache(key: string): IMainHistoryCacheEntry | undefined {
  return mainHistoryCacheStore.read(key);
}

function writeMainHistoryCache(key: string, entry: IMainHistoryCacheEntry): void {
  mainHistoryCacheStore.write(key, entry);
}

function resolveMainHistoryFetchPlan({
  cacheEntry,
  intervalSec,
  currentTimeSec,
}: {
  cacheEntry: IMainHistoryCacheEntry | undefined;
  intervalSec: number;
  currentTimeSec: number;
}): MainHistoryFetchPlan {
  return resolveChartHistoryFetchPlan({
    cacheEntry,
    intervalSec,
    currentTimeSec,
    staleIntervals: MAIN_HISTORY_STALE_INTERVALS,
  });
}

function buildMainChartView(
  cacheEntry: IMainHistoryCacheEntry,
  poolStatsSnapshot: IMainPoolStatsSnapshot | undefined
): IDomainPoolStatHistories {
  const view = buildChartHistoryView(
    cacheEntry,
    MAIN_HISTORY_MAX_INTERVALS,
    poolStatsSnapshot
  );

  return {
    suffix: view.suffix,
    histories: view.histories,
    allZeros: view.histories.every((point) => point.y == 0),
  };
}

function provideFooter(
  isLastPage: boolean,
  isTableLoading: boolean,
  canLoadMore: boolean,
  currentPage: number,
  callback: () => void
): React.ReactNode | null {
  if (!canLoadMore) return null;
  if (!isLastPage) return null;
  if (currentPage == 1) return null;

  return (
    <div>
      <Button
        block
        size="middle"
        type="text"
        style={{ margin: "0 auto", color: "#1890ff" }}
        disabled={isTableLoading}
        onClick={() => callback()}
      >
        <FormattedMessage id="action-load-more" />
      </Button>
    </div>
  );
}

type CoinMetaTone = "ok" | "bad" | "muted" | "neutral";

function providePpsAvailableMeta(
  info: ICoin | undefined,
  coinIsAlgorithm: boolean,
  availableLabel: string,
  unavailableLabel: string
): { value: string; tone: CoinMetaTone } {
  if (coinIsAlgorithm) return { value: "—", tone: "muted" };
  if (info?.ppsAvailable == undefined) return { value: "N/A", tone: "muted" };
  return info.ppsAvailable
    ? { value: availableLabel, tone: "ok" }
    : { value: unavailableLabel, tone: "bad" };
}

function provideFeeMeta(
  value: number | undefined,
  coinIsAlgorithm: boolean
): { value: string; tone: CoinMetaTone } {
  if (coinIsAlgorithm) return { value: "—", tone: "muted" };
  if (value == undefined || Number.isFinite(value) == false) {
    return { value: "N/A", tone: "muted" };
  }
  const formatted = value.toFixed(3).replace(/\.?0+$/, "");
  return { value: `${formatted}%`, tone: "neutral" };
}

function provideFeePairMeta(
  pplnsFee: number | undefined,
  ppsFee: number | undefined,
  coinIsAlgorithm: boolean
): { pplnsValue: string; ppsValue: string; tone: CoinMetaTone } {
  if (coinIsAlgorithm) {
    return { pplnsValue: "—", ppsValue: "—", tone: "muted" };
  }
  const pplns = provideFeeMeta(pplnsFee, false);
  const pps = provideFeeMeta(ppsFee, false);
  const tone =
    pplns.tone == "muted" && pps.tone == "muted" ? "muted" : "neutral";
  return { pplnsValue: pplns.value, ppsValue: pps.value, tone };
}

function provideProfitabilityScaleMeta(
  algorithm: string | undefined,
  coin: string | undefined
): { value: string; unit: string } {
  const baseCoin = getBaseCoinName(coin).toUpperCase();
  if (baseCoin == "XPM") {
    return { value: "1", unit: "cpd" };
  }

  if (algorithm == "sha256") {
    return { value: "100", unit: "TH" };
  }

  if (algorithm == "scrypt") {
    return { value: "1", unit: "GH" };
  }

  return { value: "1", unit: "TH" };
}

function provideAutoExchangeMeta(
  coinIsAlgorithm: boolean,
  unavailableLabel: string
): { value: string; tone: CoinMetaTone } {
  if (coinIsAlgorithm) return { value: "—", tone: "muted" };
  return { value: unavailableLabel, tone: "muted" };
}

function providePoolStatMeta(
  info: IDomainPoolStat[] | undefined,
  title: string
): { value: string; tone: CoinMetaTone } {
  if (!info || info.length == 0) return { value: "—", tone: "muted" };
  const found = info.find((item) => item.title == title);
  if (!found || !found.value) return { value: "—", tone: "muted" };
  return { value: found.value, tone: "neutral" };
}

function provideMetaToneClass(
  styleMap: Record<string, string>,
  tone: CoinMetaTone
): string {
  if (tone == "ok") return styleMap.coinFeeValueOk;
  if (tone == "bad") return styleMap.coinFeeValueBad;
  if (tone == "muted") return styleMap.coinFeeValueMuted;
  return styleMap.coinFeeValueNeutral;
}
